
import React, { useEffect, useState } from 'react';
import { Order, OrderStatus } from '../types';
import { api } from '../services/api';
import { STLViewer } from './STLViewer';
import { Download, FileBox, ArrowLeft, Phone } from 'lucide-react';

interface PublicOrderViewProps {
  token: string;
}

export const PublicOrderView = ({ token }: PublicOrderViewProps) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getOrderByToken(token)
      .then(data => {
         if (data) setOrder(data);
         else setError('Order not found or link expired.');
      })
      .catch(err => setError('Failed to load order.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Loading order data...</div>;
  if (error || !order) return <div className="min-h-screen bg-black flex items-center justify-center text-red-400">{error}</div>;

  const stlUrl = order.files.find(f => f.name.toLowerCase().endsWith('.stl'))?.url;

  return (
    <div className="min-h-screen bg-background text-zinc-100 p-6">
       <div className="max-w-5xl mx-auto space-y-6">
          <header className="flex justify-between items-center bg-zinc-900 p-6 rounded-2xl border border-white/10">
             <div>
                <h1 className="text-2xl font-bold text-white mb-1">Production Order #{order.toothNumber || order.id.slice(0,6)}</h1>
                <p className="text-zinc-400">Patient: {order.patientName}</p>
             </div>
             <div className="text-right">
                <div className="text-sm text-zinc-500 uppercase font-bold">Due Date</div>
                <div className="text-white font-bold">{order.dueDate ? new Date(order.dueDate).toLocaleDateString() : 'ASAP'}</div>
             </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             {/* Left: 3D View */}
             <div className="bg-black rounded-2xl overflow-hidden border border-white/10 aspect-square lg:aspect-auto h-[400px] relative">
                <STLViewer url={stlUrl} />
             </div>

             {/* Right: Details & Downloads */}
             <div className="space-y-6">
                <div className="bg-zinc-900 p-6 rounded-2xl border border-white/10 space-y-4">
                   <h3 className="font-bold text-white border-b border-white/5 pb-2">Case Specifications</h3>
                   
                   <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                         <label className="text-zinc-500 block text-xs uppercase">Shade</label>
                         <span className="text-white font-medium">{order.shade}</span>
                      </div>
                      <div>
                         <label className="text-zinc-500 block text-xs uppercase">Material</label>
                         <span className="text-white font-medium">{order.isPMMA ? 'PMMA' : 'Zirconia/Emax'}</span>
                      </div>
                      <div className="col-span-2">
                         <label className="text-zinc-500 block text-xs uppercase">Items</label>
                         <div className="space-y-2 mt-1">
                            {order.items.map((item, idx) => (
                               <div key={idx} className="bg-black/30 p-2 rounded border border-white/5">
                                  <div className="flex justify-between">
                                     <span>{item.serviceName}</span>
                                     <span className="font-mono text-zinc-300">{item.teeth.join(', ')}</span>
                                  </div>
                                  {item.details && <div className="text-[10px] text-zinc-500 mt-1 pl-2 border-l border-zinc-700">{item.details}</div>}
                               </div>
                            ))}
                         </div>
                      </div>
                   </div>
                </div>

                <div className="bg-zinc-900 p-6 rounded-2xl border border-white/10">
                   <h3 className="font-bold text-white mb-4">Downloads</h3>
                   <div className="space-y-3">
                      {order.designFiles?.productionZip && (
                         <a 
                           href={order.designFiles.productionZip} 
                           target="_blank" 
                           className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-xl hover:bg-green-900/30 transition-colors group"
                         >
                            <div className="flex items-center gap-3">
                               <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center text-green-400">
                                  <FileBox size={20}/>
                               </div>
                               <div>
                                  <div className="font-bold text-green-100">Production Files (ZIP)</div>
                                  <div className="text-xs text-green-400/70">Includes STL for Nesting</div>
                               </div>
                            </div>
                            <Download size={20} className="text-green-400 group-hover:scale-110 transition-transform"/>
                         </a>
                      )}

                      {order.files.map(f => (
                         <a 
                           key={f.id}
                           href={f.url} 
                           target="_blank" 
                           className="flex items-center justify-between p-3 bg-black/40 border border-white/5 rounded-xl hover:bg-white/5 transition-colors"
                         >
                            <span className="text-sm text-zinc-300 truncate max-w-[200px]">{f.name}</span>
                            <Download size={16} className="text-zinc-500 hover:text-white"/>
                         </a>
                      ))}
                   </div>
                </div>
                
                <div className="bg-blue-900/20 p-4 rounded-xl border border-blue-500/20 text-center">
                   <p className="text-blue-200 text-sm mb-2">Need help with this case?</p>
                   <a href="https://galaldentallab.com" className="text-blue-400 font-bold text-sm hover:underline">Contact Lab Support</a>
                </div>
             </div>
          </div>
       </div>
    </div>
  );
};
